import CostCenterPeriodSpend from "./CostCenterPeriodSpend";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import {
  fetchCostCenter,
  fetchCostCenterAssignments,
  fetchCostCenterSourceCoverageDetail,
} from "../api/costCenters";
import type {
  CostCenterAssignment,
  CostCenterEntity,
  CostCenterSourceCoverage,
} from "../api/costCenters";
import CostCenterEditDrawer from "../components/CostCenterEditDrawer";
import type { EditableCostCenter } from "../components/CostCenterEditDrawer";
import { ApiError } from "../lib/api";
import { isDemoMode } from "../lib/env";

import { DetailBackNav, DetailQueryStatus } from "../components/DetailPageNav";
import {
  CoverageReasonPill,
  CoverageStatusBadge,
  SourceCoveragePills,
  splitCoverageCodes,
} from "../components/SourceCoverage";
import Spinner from "../components/Spinner";
import {
  backTargetFromLocationState,
  type DetailLocationState,
} from "../lib/backNavigation";
import {
  costCenterHref,
  costCenterTypeLabel,
  displayCostCenterRawReference,
  displayCostCenterTitle,
} from "../lib/costCenterDisplay";

import { formatDate, formatEuro, formatQty } from "../lib/format";

type DetailView = "overview" | "details";

function decodeCostCenterKey(value: string | undefined): string {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default function CostCenterDetailPage() {
  const { costCenterKey } = useParams<{ costCenterKey: string }>();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("return_to");
  const fromState = returnTo && (returnTo === "/cost-centers" || returnTo.startsWith("/cost-centers?")) ? returnTo : (location.state as DetailLocationState)?.from;
  const back = backTargetFromLocationState({
    from: fromState,
    fallback: { to: "/cost-centers", label: "← zurück zu den Kostenstellen" },
  });
  const [editingCostCenter, setEditingCostCenter] =
    useState<EditableCostCenter | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const detailView: DetailView = searchParams.get("tab") === "details" ? "details" : "overview";
  const setDetailView = (view: DetailView) => {
    const next = new URLSearchParams(searchParams);
    if (view === "details") next.set("tab", "details"); else next.delete("tab");
    setSearchParams(next, { state: location.state });
  };
  const key = decodeCostCenterKey(costCenterKey);
  const requestedCostCenterType = new URLSearchParams(location.search).get("type");
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  const entityQuery = useQuery({
    queryKey: ["cost-center", key, requestedCostCenterType],
    queryFn: () => fetchCostCenter(key, requestedCostCenterType),
    enabled: key.length > 0,
  });

  const sourceCoverageQuery = useQuery({
    queryKey: [
      "cost-center",
      key,
      requestedCostCenterType,
      "source-coverage",
    ],
    queryFn: () =>
      fetchCostCenterSourceCoverageDetail(key, requestedCostCenterType),
    enabled: key.length > 0,
  });

  const entity = entityQuery.data;
  const openEditor = () => {
    if (!entity) return;
    setEditingCostCenter({
      costCenterType: entity.cost_center_type,
      costCenterKey: entity.cost_center_key,
      displayLabel: entity.display_label,
      sourceDisplayLabel: entity.source_display_label ?? null,
      manualDisplayLabel: entity.manual_display_label ?? null,
      customerAnchorLabel: entity.customer_anchor_label ?? null,
      projectRole: entity.project_role ?? null,
    });
  };

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <DetailBackNav
        back={back}
        home={{ to: "/cost-centers", label: "Kostenstellen" }}
      />

      <DetailQueryStatus
        isLoading={entityQuery.isLoading}
        error={entityQuery.isError ? entityQuery.error : null}
        loadingLabel="Lade Kostenstelle …"
        notFoundLabel="Kostenstelle nicht gefunden."
      />

      {entity ? (
        <>
          <DetailHeader
            entity={entity}
            onEdit={isDemoMode() ? undefined : openEditor}
          />

          <DetailTabs view={detailView} onChange={setDetailView} />

          {detailView === "overview" ? (
            <div role="tabpanel" aria-label="Übersicht">
              <CostCenterPeriodSpend costCenterKey={entity.cost_center_key} costCenterType={entity.cost_center_type} costCenterLabel={displayCostCenterTitle(entity.cost_center_type, entity.cost_center_key, entity.display_label, { customerAnchorLabel: entity.customer_anchor_label, manualLabel: entity.manual_display_label })} />
            </div>
          ) : (
            <div role="tabpanel" aria-label="Details" className="pb-32 md:pb-12">
              <AssignmentsSection
                costCenterType={entity.cost_center_type}
                costCenterKey={entity.cost_center_key}
                fromPath={currentPath}
                onEdit={isDemoMode() ? undefined : openEditor}
              />

              <DetailDisclosure summary="Quellen & Nachweise">
                <SourceEvidence
                  entity={entity}
                  coverage={sourceCoverageQuery.data ?? null}
                  isLoading={sourceCoverageQuery.isLoading}
                  isError={sourceCoverageQuery.isError}
                />
              </DetailDisclosure>
            </div>
          )}

          <CostCenterEditDrawer
            entity={editingCostCenter}
            onClose={() => setEditingCostCenter(null)}
          />
        </>
      ) : null}
    </main>
  );
}

function DetailTabs({
  view,
  onChange,
}: {
  view: DetailView;
  onChange: (next: DetailView) => void;
}) {
  const tabs: { value: DetailView; label: string }[] = [
    { value: "overview", label: "Übersicht" },
    { value: "details", label: "Details" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Kostenstellen-Ansicht"
      className="mt-6 flex gap-1 border-b border-slate-200"
    >
      {tabs.map((tab) => {
        const active = tab.value === view;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              active
                ? "border-emerald-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function DetailDisclosure({
  summary,
  defaultOpen = false,
  children,
}: {
  summary: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="group mt-6" open={defaultOpen}>
      <summary className="cursor-pointer list-none border-b border-slate-200 text-sm font-semibold text-slate-700 marker:content-none">
        <span className="flex items-center justify-between gap-2 py-2">
          <span>{summary}</span>
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-4 w-4 text-slate-400 transition group-open:rotate-180"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </summary>
      {children}
    </details>
  );
}

function DetailHeader({
  entity,
  onEdit,
}: {
  entity: CostCenterEntity;
  onEdit?: () => void;
}) {
  const title = displayCostCenterTitle(
    entity.cost_center_type,
    entity.cost_center_key,
    entity.display_label,
    {
      customerAnchorLabel: entity.customer_anchor_label,
      manualLabel: entity.manual_display_label,
    },
  );
  const rawReference = entity.customer_anchor_label
    ? displayCostCenterRawReference(
        entity.cost_center_type,
        entity.cost_center_key,
        entity.source_display_label ?? entity.display_label,
      )
    : null;
  return (
    <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {costCenterTypeLabel(entity.cost_center_type)}
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">{title}</h1>
        {rawReference && rawReference !== title ? (
          <p className="mt-1 text-sm text-slate-500">Referenz: {rawReference}</p>
        ) : null}
        <p className="mt-1 text-sm text-slate-500">
          Quellenbasierter Kontext aus Belegen, Stamm- und manuellen Daten.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Kostenstelle bearbeiten"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 shadow-sm transition hover:bg-emerald-100"
          >
            <DetailPencilIcon />
            Bearbeiten
          </button>
        )}

      </div>
    </header>
  );
}

function DetailPencilIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}


function SourceEvidence({
  entity,
  coverage,
  isLoading,
  isError,
}: {
  entity: CostCenterEntity;
  coverage: CostCenterSourceCoverage | null;
  isLoading: boolean;
  isError: boolean;
}) {
  const gapCodes = splitCoverageCodes(coverage?.gap_codes);
  const unsupportedCodes = splitCoverageCodes(coverage?.unsupported_source_codes);
  const coverageCount = (
    value: string | number | null | undefined,
  ): string => (coverage ? formatCount(value) : "–");
  return (
    <section className="pt-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Quellenabdeckung
          </h2>
          {coverage ? (
            <div className="mt-1">
              <CoverageStatusBadge status={coverage.coverage_status} />
            </div>
          ) : null}
        </div>
        {isLoading ? (
          <span className="text-xs text-slate-400">Abdeckung wird geladen...</span>
        ) : null}
        {isError ? (
          <span className="text-xs text-rose-600">
            Abdeckung konnte nicht geladen werden.
          </span>
        ) : null}
      </div>
      <div className="mt-3 grid gap-4 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <div className="text-slate-500">Quellen</div>
          <div className="mt-2">
            <SourcePills entity={entity} coverage={coverage} />
          </div>
          {gapCodes.length > 0 || unsupportedCodes.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {gapCodes.map((code) => (
                <CoverageReasonPill key={code} code={code} />
              ))}
              {unsupportedCodes.map((code) => (
                <CoverageReasonPill key={code} code={code} />
              ))}
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Fact
            label="PDF-Belege"
            value={coverageCount(coverage?.pdf_document_count)}
          />
          <Fact
            label="PDF-Positionen"
            value={coverageCount(coverage?.pdf_line_count)}
          />
          <Fact
            label="Zuordnungen"
            value={coverageCount(coverage?.relationship_candidate_count)}
          />
          <Fact
            label="Offen"
            value={coverageCount(coverage?.open_relationship_count)}
          />
          <Fact label="Letzter Beleg" value={formatDate(entity.last_observed_date)} />
          <Fact
            label="Manuell"
            value={coverageCount(coverage?.manual_decision_count)}
          />
        </div>
      </div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function AssignmentsSection({
  costCenterType,
  costCenterKey,
  fromPath,
  onEdit,
}: {
  costCenterType: string;
  costCenterKey: string;
  fromPath: string;
  onEdit?: () => void;
}) {
  const query = useQuery({
    queryKey: ["cost-center-assignments", costCenterType, costCenterKey],
    queryFn: () =>
      fetchCostCenterAssignments({ costCenterType, costCenterKey }),
    enabled: costCenterKey.length > 0,
    retry: false,
    staleTime: 30_000,
  });
  const data = query.data;
  const assignments = data?.assignments ?? [];
  const aggregates = data?.aggregates ?? null;
  const suggestionCount = data?.suggestion_count ?? 0;
  const groupingUnavailable =
    query.isError && isCostCenterAssignmentsUnavailable(query.error);

  return (
    <section className="mt-8 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">
            Zugeordnete Kostenstellen
          </h2>
          {aggregates ? (
            <p className="mt-0.5 text-xs text-slate-500">
              Auswertung{" "}
              <span className="font-semibold tabular-nums text-slate-700">
                {formatEuro(aggregates.evaluation_spend_last_year)}
              </span>
              <span className="text-slate-400">
                {"  ·  "}Eigenwert {formatEuro(aggregates.own_spend_last_year)}
                {"  ·  "}Zugeordnet{" "}
                {formatEuro(aggregates.assigned_spend_last_year)}
              </span>
            </p>
          ) : null}
        </div>

      </div>
      <div className="divide-y divide-slate-100">
        {query.isLoading ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <Spinner /> Lade Zuordnungen ...
            </span>
          </div>
        ) : groupingUnavailable ? (
          <div className="px-4 py-6 text-center text-sm text-slate-500">
            Zuordnungen sind in dieser Backend-Konfiguration noch nicht aktiv.
          </div>
        ) : query.isError ? (
          <div className="px-4 py-6 text-center text-sm text-rose-600">
            Zuordnungen konnten nicht geladen werden.
          </div>
        ) : assignments.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">
            Keine Kostenstellen zugeordnet.
          </div>
        ) : (
          assignments.map((assignment) => (
            <AssignmentRow
              key={`${assignment.origin}:${assignment.cost_center_type}:${assignment.cost_center_key}`}
              assignment={assignment}
              fromPath={fromPath}
            />
          ))
        )}
      </div>
      {suggestionCount > 0 && !groupingUnavailable ? (
        <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          {suggestionCount}{" "}
          {suggestionCount === 1 ? "mögliche Zuordnung" : "mögliche Zuordnungen"}{" "}
          {onEdit ? <>– über „<button type="button" className="text-emerald-800 underline underline-offset-2" onClick={onEdit}>Bearbeiten</button>“ prüfbar.</> : "– Prüfung im Demo-Modus nicht verfügbar."}
        </div>
      ) : null}
    </section>
  );
}

function AssignmentRow({
  assignment,
  fromPath,
}: {
  assignment: CostCenterAssignment;
  fromPath: string;
}) {
  const href = costCenterHref(
    assignment.cost_center_type,
    assignment.cost_center_key,
  );
  const title = displayCostCenterTitle(
    assignment.cost_center_type,
    assignment.cost_center_key,
    assignment.display_label,
    { manualLabel: assignment.manual_display_label },
  );
  const isAuto = assignment.origin === "auto";
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span
        className={`flex-none rounded px-1.5 py-0.5 text-[11px] font-medium ${
          isAuto
            ? "bg-emerald-50 text-emerald-700"
            : "bg-slate-100 text-slate-600"
        }`}
      >
        {isAuto ? "Auto" : "Manuell"}
      </span>
      <div className="min-w-0 flex-1">
        {href ? (
          <Link
            to={href}
            state={{ from: fromPath }}
            className="block truncate font-medium text-slate-900 hover:underline"
            title={title}
          >
            {title}
          </Link>
        ) : (
          <span
            className="block truncate font-medium text-slate-900"
            title={title}
          >
            {title}
          </span>
        )}
        <div className="mt-0.5 text-xs text-slate-500">
          {costCenterTypeLabel(assignment.cost_center_type)}
        </div>
      </div>
      <div className="flex-none text-right">
        <div className="text-sm tabular-nums text-slate-700">
          {formatEuro(assignment.spend_last_year)}
        </div>
        <div className="text-xs text-slate-400">12 M.</div>
      </div>
    </div>
  );
}

function isCostCenterAssignmentsUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  if (error.status === 404) return true;
  return (
    error.status === 503 &&
    (message === "cost_center_groupings_require_postgres" ||
      message.includes("postgres_url fehlt") ||
      message.includes("postgres-read-backend"))
  );
}

function SourcePills({
  entity,
  coverage,
}: {
  entity: CostCenterEntity;
  coverage?: CostCenterSourceCoverage | null;
}) {
  return (
    <SourceCoveragePills
      emptyLabel="Keine Quellen markiert"
      sources={[
        {
          key: "pdf",
          label: "PDF",
          active: coverage?.has_pdf_evidence ?? entity.has_pdf_evidence,
          tone: "pdf",
        },
        {
          key: "ugl",
          label: "UGL",
          active: coverage?.has_ugl_evidence ?? entity.has_ugl_evidence,
          tone: "ugl",
        },
        {
          key: "datanorm",
          label: "DATANORM",
          active: coverage?.has_datanorm_evidence,
          tone: "datanorm",
        },
        {
          key: "manual",
          label: "Manuell",
          active: coverage?.has_manual_evidence ?? entity.has_manual_evidence,
          tone: "manual",
        },
      ]}
    />
  );
}

function formatCount(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "–";
  return formatQty(value);
}
